import Cookies from 'js-cookie'

import { maLogger } from '@/base/logger'

/**
 * Creates a proxy-based cookie manager using js-cookie for the given key mapping.
 * All missing or unconfigured keys return `null` for consistent checks.
 *
 * @example
 * ```js
 * import { createCookieManager } from '@/utils/storageManagerFactory'
 * import { COOKIE_KEYS } from '@/utils/constants'
 *
 * const cookieManager = createCookieManager(COOKIE_KEYS, {
 *   prefix: 'ma_',                 // cookie names: ma_<key><suffix>
 *   suffix: '_staging',            // e.g. '_prod' or '_test'
 *   placeholderData: { theme: 'light' },
 *   globalCookieOptions: { expires: 7, secure: true, sameSite: 'lax' }
 * })
 *
 * console.log(cookieManager.getFullName('user'))  // 'ma_user_staging'
 * console.log(cookieManager.theme)               // 'light' if not set, otherwise null
 * cookieManager.user = { id: 123 }
 * console.log(cookieManager.user)                // { id: 123 }
 * delete cookieManager.user
 * cookieManager.clear()
 * ```
 *
 * @param {Record<string, string|{ name: string; options?: import('js-cookie').CookieAttributes }>} keys
 * Mapping from property names to either cookie key string or config object
 * with name and per-key options.
 * @param {{ prefix?: string; suffix?: string; placeholderData?: Record<string, any>; globalCookieOptions?: import('js-cookie').CookieAttributes }} [options]
 * @returns {{ [key: string]: any } & { clear: () => void; getFullName: (key: string) => string }} Proxy for cookie access.
 */
export function createCookieManager(keys, options = {}) {
  const {
    prefix = '',
    suffix = '',
    placeholderData = {},
    globalCookieOptions = {},
  } = options

  // Utility to assemble full cookie name
  function getFullName(name) {
    return `${prefix}${name}${suffix}`
  }

  const manager = {
    /** Remove all configured cookies */
    clear() {
      for (const prop in keys) {
        const { name, options: keyOpts } = normalizeKeyConfig(keys[prop])
        Cookies.remove(getFullName(name), { ...globalCookieOptions, ...keyOpts })
      }
    },
    /** Get the full cookie name for a given key */
    getFullName,
  }

  return new Proxy(manager, {
    get(target, prop) {
      // Return manager methods
      if (prop in target)
        return target[prop]

      const config = normalizeKeyConfig(keys[prop])
      if (!config) {
        maLogger.warn(`No cookie key found for property "${String(prop)}".`)
        return null
      }

      const fullName = getFullName(config.name)
      const raw = Cookies.get(fullName)

      // Always return null for missing cookies to keep type consistent
      if (raw === undefined) {
        return Object.prototype.hasOwnProperty.call(placeholderData, prop)
          ? placeholderData[prop]
          : null
      }

      try {
        return JSON.parse(raw)
      }
      catch (err) {
        maLogger.warn(`Failed to parse JSON for cookie "${fullName}".`, err)
        return raw
      }
    },

    set(_target, prop, value) {
      const config = normalizeKeyConfig(keys[prop])
      if (!config) {
        maLogger.warn(`No cookie key found for property "${String(prop)}".`)
        return false
      }
      const fullName = getFullName(config.name)
      try {
        const toStore = JSON.stringify(value)
        Cookies.set(fullName, toStore, { ...globalCookieOptions, ...config.options })
      }
      catch (err) {
        maLogger.warn(`Failed to stringify value for cookie "${fullName}".`, err)
      }
      return true
    },

    deleteProperty(_target, prop) {
      const config = normalizeKeyConfig(keys[prop])
      if (!config) {
        maLogger.warn(`No cookie key found for property "${String(prop)}".`)
        return false
      }
      const fullName = getFullName(config.name)
      Cookies.remove(fullName, { ...globalCookieOptions, ...config.options })
      return true
    },
  })
}

/**
 * Normalize key entry to a uniform object.
 */
function normalizeKeyConfig(entry) {
  if (typeof entry === 'string') {
    return { name: entry, options: {} }
  }
  if (entry && typeof entry.name === 'string') {
    return { name: entry.name, options: entry.options || {} }
  }
  return null
}
